import React, { useEffect, useState, useRef, useMemo, memo, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link, useLocation } from 'react-router-dom';
import {
  Box, Typography, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, CircularProgress, Pagination,
  Avatar, TextField, MenuItem, Button, Dialog, DialogActions,
  DialogContent, DialogTitle, IconButton, Checkbox, Tooltip,
  Snackbar, Drawer, Alert, Autocomplete,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { useTheme, useMediaQuery } from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import moment from 'moment';
import 'moment-timezone';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
import PhoneInput from 'react-phone-input-2';
import 'react-phone-input-2/lib/style.css';
import { parsePhoneNumberFromString } from 'libphonenumber-js';

import AccessTimeIcon             from '@mui/icons-material/AccessTime';
import ScheduleIcon               from '@mui/icons-material/Schedule';
import PersonAddIcon              from '@mui/icons-material/PersonAdd';
import SendIcon                   from '@mui/icons-material/Send';
import ContentCopyIcon            from '@mui/icons-material/ContentCopy';
import OpenInNewIcon              from '@mui/icons-material/OpenInNew';
import CheckIcon                  from '@mui/icons-material/Check';
import EditNoteIcon               from '@mui/icons-material/EditNote';
import AssignmentTurnedInIcon     from '@mui/icons-material/AssignmentTurnedIn';
import TransferWithinAStationIcon from '@mui/icons-material/TransferWithinAStation';
import SevereColdIcon             from '@mui/icons-material/SevereCold';
import FilterListIcon             from '@mui/icons-material/FilterList';
import RefreshIcon                from '@mui/icons-material/Refresh';
import SettingsIcon               from '@mui/icons-material/Settings';
import AddIcon                    from '@mui/icons-material/Add';
import CloseIcon                  from '@mui/icons-material/Close';
import VisibilityIcon             from '@mui/icons-material/Visibility';
import VisibilityOffIcon          from '@mui/icons-material/VisibilityOff';
import ArrowDownwardIcon          from '@mui/icons-material/ArrowDownward';
import ArrowUpwardIcon            from '@mui/icons-material/ArrowUpward';
import ClearIcon                  from '@mui/icons-material/Clear';
import AssignmentIcon             from '@mui/icons-material/Assignment';
import WhatsAppIcon               from '@mui/icons-material/WhatsApp';
import PhoneIcon                  from '@mui/icons-material/Phone';
import CheckCircleIcon            from '@mui/icons-material/CheckCircle';
import BusinessCenterIcon         from '@mui/icons-material/BusinessCenter';
import PersonIcon                 from '@mui/icons-material/Person';
import DragIndicatorIcon          from '@mui/icons-material/DragIndicator';

// Actions
import { GetallLeads_data_By_Managers_Email } from '../Redux/Actions/RoleData_Actions';
import { GetallProjectsdata }                 from '../Redux/Actions/Projects_Action';
import { UpdateClientAction, UpdateClientActionToReaciveusertolead } from '../Redux/Actions/UsersAction';

// Components
import { ShowUserSheet }                  from './ShowUserSheet';
import { ShowClientsActionsAdvanced }     from './ShowClientsActionsAdvanced';
import { PrivateSheetForUser }            from './PrivateSheetForUser';
import { ManagerQuickFilterModal }        from './ManagerQuickFilterModal';
import { AssignLeadsFromManagerModel }    from './AssignLeadsFromManagerModel';
import { Notify }                         from '../Utility/Notify';
import { useSidebarWidth }                from '../Utility/useSidebarWidth';

// ─── Design Tokens (same as TeamLeader) ──────────────────────────────────────
const C = {
  bg:          '#F7F8FC',
  white:       '#FFFFFF',
  border:      '#E8EAF0',
  accent:      '#0D47A1',
  accentDark:  '#1330A6',
  accentSoft:  'rgba(13,71,161,0.07)',
  text:        '#1C2340',
  textMuted:   '#7B829A',
  success:     '#12B76A',
  successSoft: 'rgba(18,183,106,0.08)',
  warning:     '#F79009',
  danger:      '#F04438',
  dangerSoft:  'rgba(240,68,56,0.08)',
  shadow:      '0 1px 3px rgba(16,24,40,0.08)',
};

const SIDEBAR_COLLAPSED = 64;

const StyledRow = styled(TableRow)(() => ({
  '&:hover': { backgroundColor: 'rgba(13,71,161,0.03)' },
  '& td':    { borderBottom: `1px solid ${C.border}` },
}));

// ─── Stat Card ────────────────────────────────────────────────────────────────
const StatCard = ({ label, value, highlight }) => (
  <Box sx={{
    bgcolor: highlight ? C.accent : C.white,
    border:  `1px solid ${highlight ? C.accent : C.border}`,
    borderRadius: '12px', p: 2.5,
    width: 160, minWidth: 160, height: 120, minHeight: 120,
    boxShadow: highlight ? '0 4px 16px rgba(13,71,161,0.25)' : C.shadow,
    flexShrink: 0, display: 'flex', flexDirection: 'column', justifyContent: 'space-between',
  }}>
    <Typography sx={{ fontSize: '11px', fontWeight: 500, color: highlight ? 'rgba(255,255,255,0.75)' : C.textMuted, letterSpacing: '0.06em', textTransform: 'uppercase', fontFamily: "'DM Sans', sans-serif" }}>{label}</Typography>
    <Typography sx={{ fontSize: '26px', fontWeight: 700, color: highlight ? '#fff' : C.text, fontFamily: "'DM Sans', sans-serif", lineHeight: 1.2 }}>{value != null ? String(value) : '—'}</Typography>
    {highlight && <Box sx={{ height: '3px', width: '40px', bgcolor: C.warning, borderRadius: '2px' }} />}
  </Box>
);

// ─── Phone Cell ───────────────────────────────────────────────────────────────
const PhoneCell = ({ user }) => {
  const [show,   setShow]   = useState(false);
  const [copied, setCopied] = useState(false);
  const phone = user?.phone || '';

  const handleCopy = (e) => {
    e.stopPropagation();
    navigator.clipboard.writeText(phone).then(() => {
      setCopied(true);
      Notify('success', 'Phone copied!', 'success');
      setTimeout(() => setCopied(false), 2000);
    });
  };

  return (
    <Box onMouseEnter={() => setShow(true)} onMouseLeave={() => setShow(false)}>
      <Typography sx={{ fontSize: '13px', color: copied ? C.success : C.text, mb: '4px', fontFamily: "'DM Sans', sans-serif" }}>
        {show ? phone : `${phone.substring(0, 6)}···`}
      </Typography>
      <Box sx={{ display: 'flex', gap: '6px' }}>
        <IconButton size="small"
          onClick={e => { e.stopPropagation(); window.open(`https://wa.me/${phone.replace(/\D/g, '')}`, '_blank'); }}
          sx={{ width: 26, height: 26, bgcolor: 'rgba(37,211,102,0.1)', color: '#25D366', borderRadius: '7px', '&:hover': { bgcolor: 'rgba(37,211,102,0.2)' } }}>
          <WhatsAppIcon sx={{ fontSize: 14 }} />
        </IconButton>
        <IconButton size="small"
          onClick={e => { e.stopPropagation(); window.open(`tel:${phone}`); }}
          sx={{ width: 26, height: 26, bgcolor: C.accentSoft, color: C.accent, borderRadius: '7px', '&:hover': { bgcolor: 'rgba(13,71,161,0.15)' } }}>
          <PhoneIcon sx={{ fontSize: 14 }} />
        </IconButton>
        <IconButton size="small" onClick={handleCopy}
          sx={{ width: 26, height: 26, bgcolor: copied ? C.successSoft : 'rgba(247,144,9,0.08)', color: copied ? C.success : C.warning, borderRadius: '7px' }}>
          {copied ? <CheckCircleIcon sx={{ fontSize: 14 }} /> : <ContentCopyIcon sx={{ fontSize: 14 }} />}
        </IconButton>
      </Box>
    </Box>
  );
};

// ─── Stage Cell ───────────────────────────────────────────────────────────────
const StageCell = ({ user }) => {
  const stageName = user.stage?.name || '';
  const isPast    = user.stagedateupdated && moment(user.stagedateupdated).isBefore(moment());

  return (
    <Box>
      {stageName && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: user.stagedateupdated ? '4px' : 0 }}>
          {stageName === 'Done Deal'       ? <span style={{ fontSize: '18px' }}>🎉</span>
          : stageName === 'Pending'        ? <SevereColdIcon sx={{ color: '#9c27b0', fontSize: '18px', flexShrink: 0 }} />
          : stageName === 'Fresh'          ? <CheckIcon sx={{ fontSize: '18px', color: '#4caf50', flexShrink: 0 }} />
          : stageName === 'Transfer'       ? <TransferWithinAStationIcon sx={{ color: C.danger, fontSize: '18px', flexShrink: 0 }} />
          : stageName === 'Not Interested' ? <Box sx={{ width: 18, height: 18, borderRadius: '50%', bgcolor: '#555', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontSize: '12px', fontWeight: 700, flexShrink: 0 }}>—</Box>
          : stageName === 'EOI'            ? <EditNoteIcon sx={{ color: '#9c27b0', fontSize: '18px', flexShrink: 0 }} />
          : stageName === 'Reservation'    ? <AssignmentTurnedInIcon sx={{ color: '#9c27b0', fontSize: '18px', flexShrink: 0 }} />
          : <Box sx={{ width: 18, height: 18, borderRadius: '50%', flexShrink: 0, bgcolor: isPast ? '#CC5500' : C.accent }} />}

          <Box sx={{
            display: 'inline-flex', alignItems: 'center',
            px: '8px', py: '3px', borderRadius: '20px',
            bgcolor: stageName === 'Done Deal' ? C.successSoft : stageName === 'Pending' ? 'rgba(156,39,176,0.08)' : stageName === 'Transfer' ? C.dangerSoft : stageName === 'Not Interested' ? 'rgba(85,85,85,0.08)' : stageName === 'Fresh' ? 'rgba(76,175,80,0.08)' : isPast ? 'rgba(204,85,0,0.1)' : C.accentSoft,
            color:  stageName === 'Done Deal' ? C.success : stageName === 'Pending' ? '#9c27b0' : stageName === 'Transfer' ? C.danger : stageName === 'Not Interested' ? '#555' : stageName === 'Fresh' ? '#4caf50' : isPast ? '#CC5500' : C.accent,
          }}>
            <Typography sx={{ fontSize: '11.5px', fontWeight: 600, fontFamily: "'DM Sans', sans-serif", whiteSpace: 'nowrap' }}>{stageName}</Typography>
          </Box>
        </Box>
      )}
      {user.stagedateupdated && (
        <Typography sx={{ fontSize: '10.5px', color: C.textMuted, fontFamily: "'DM Sans', sans-serif", display: 'flex', alignItems: 'center', gap: '3px' }}>
          <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>
          {moment(user.stagedateupdated).format('DD/MM/YY HH:mm')}
        </Typography>
      )}
    </Box>
  );
};

const getInitials = (n = '') =>
  (n || '').split(' ').filter(Boolean).map(w => w[0]).join('').toUpperCase().slice(0, 2) || '?';

// ─── Default Columns ──────────────────────────────────────────────────────────
const DEFAULT_COLS = [
  { key: 'selection',     label: '',              w: 52,  visible: true },
  { key: 'name',          label: 'Name',          w: 200, visible: true },
  { key: 'phone',         label: 'Phone',         w: 150, visible: true },
  { key: 'project',       label: 'Project',       w: 150, visible: true },
  { key: 'developer',     label: 'Developer',     w: 130, visible: true },
  { key: 'campaign',      label: 'Campaign',      w: 150, visible: true },
  { key: 'chanel',        label: 'Channel',       w: 130, visible: true },
  { key: 'stage',         label: 'Stage',         w: 180, visible: true },
  { key: 'sales',         label: 'Sales',         w: 160, visible: true },
  { key: 'Creation_date', label: 'Creation Date', w: 150, visible: true },
  { key: 'actions',       label: 'Actions',       w: 280, visible: true },
];

const MANAGER_COL_KEY   = 'managerLeadsColConfig_v2';
const MANAGER_SEARCH_KEY = 'manager_search_query';

// ─── Main Component ───────────────────────────────────────────────────────────
export const LeadsForManagersPage = () => {
  const isMobile   = useMediaQuery(useTheme().breakpoints.down('md'));
  const dispatch   = useDispatch();
  const location   = useLocation();
  const sidebarW   = useSidebarWidth();

  // ── user ──
  const [userlogin, setUserlogin] = useState(null);
  useEffect(() => { const u = localStorage.getItem('user'); if (u) setUserlogin(JSON.parse(u)); }, []);

  // ── redux ──
  const leadsFromRedux = useSelector(s => s.AllRoleData.LeadsForManagers);
  const resproject     = useSelector(s => s.AllProjects.Projects);
  const resupdatelead  = useSelector(s => s.Allusers.Get_users);

  // ── state ──
  const [loading,          setLoading]          = useState(true);
  const [currentPage,      setCurrentPage]      = useState(1);
  const [itemsPerPage,     setItemsPerPage]      = useState(25);
  const [selectedLeads,    setSelectedLeads]     = useState([]);
  const [selectedLead,     setSelectedLead]      = useState(null);
  const [appliedFilters,   setAppliedFilters]    = useState({});
  const [hasFilters,       setHasFilters]        = useState(false);
  const [sortConfig,       setSortConfig]        = useState({ key: null, direction: 'ascending' });
  const [searchQuery,      setSearchQuery]       = useState(localStorage.getItem(MANAGER_SEARCH_KEY) || '');

  const [colConfig, setColConfig] = useState(() => {
    try { const s = localStorage.getItem(MANAGER_COL_KEY); return s ? JSON.parse(s) : DEFAULT_COLS; }
    catch { return DEFAULT_COLS; }
  });
  useEffect(() => { localStorage.setItem(MANAGER_COL_KEY, JSON.stringify(colConfig)); }, [colConfig]);

  // modals
  const [quickFilter,             setQuickFilter]             = useState(false);
  const [settingsOpen,            setSettingsOpen]            = useState(false);
  const [assignManagerDialogOpen, setAssignManagerDialogOpen] = useState(false);
  const [sheetOpen,               setSheetOpen]               = useState(false);
  const [privateSheetOpen,        setPrivateSheetOpen]        = useState(false);
  const [selectedLeadforsheet,    setSelectedLeadforsheet]    = useState(null);

  // receive
  const [showReceiveModal,       setShowReceiveModal]       = useState(false);
  const [selectedLeadForReceive, setSelectedLeadForReceive] = useState(null);
  const [receiveLoading,         setReceiveLoading]         = useState(false);

  // edit lead
  const [showUpdateLead,     setShowUpdateLead]     = useState(false);
  const [selectedLeadId,     setSelectedLeadId]     = useState(null);
  const [leadnameupdate,     setLeadnameupdate]     = useState('');
  const [phonenumber2,       setPhonenumber2]       = useState('');
  const [whatsappnumber,     setWhatsappnumber]     = useState('');
  const [projectupdatelead,  setProjectupdatelead]  = useState(null);
  const [loadingUpdate,      setLoadingUpdate]      = useState(true);
  const [loadingUpdate2,     setLoadingUpdate2]     = useState(false);
  const [errPhone2,          setErrPhone2]          = useState('');
  const [errWhatsapp,        setErrWhatsapp]        = useState('');

  const searchTimerRef  = useRef(null);
  const lastSearchRef   = useRef('');
  const filtersTimerRef = useRef(null);
  const selectedLeadsRef = useRef([]);

  // ── col helpers ──
  const toggleColVisibility = key => setColConfig(p => p.map(c => c.key === key ? { ...c, visible: !c.visible } : c));
  const updateColWidth = (key, w) => setColConfig(p => p.map(c => c.key === key ? { ...c, w: Math.max(60, Math.min(400, parseInt(w) || c.w)) } : c));
  const onDragEnd = result => {
    if (!result.destination) return;
    const items = Array.from(colConfig);
    const [moved] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, moved);
    setColConfig(items);
  };

  // ── data helpers ──
  const getLeadsArray = () => {
    if (!leadsFromRedux) return [];
    if (leadsFromRedux.data?.leads && Array.isArray(leadsFromRedux.data.leads)) return leadsFromRedux.data.leads;
    if (leadsFromRedux.data?.data  && Array.isArray(leadsFromRedux.data.data))  return leadsFromRedux.data.data;
    if (leadsFromRedux.data        && Array.isArray(leadsFromRedux.data))        return leadsFromRedux.data;
    if (Array.isArray(leadsFromRedux))                                            return leadsFromRedux;
    return [];
  };
  const getPagination = () => {
    if (!leadsFromRedux) return null;
    if (leadsFromRedux.data?.pagination) return leadsFromRedux.data.pagination;
    if (leadsFromRedux.pagination)       return leadsFromRedux.pagination;
    return null;
  };

  // ── fetch ──
  const loadLeads = async (page = 1, limit = itemsPerPage, search = searchQuery, filters = appliedFilters) => {
    if (!userlogin?.data?.email) return;
    setLoading(true);
    try {
      let q = `page=${page}&limit=${limit}`;
      if (search?.trim()) q += `&keyword=${encodeURIComponent(search.trim().replace(/\+/g, ''))}`;
      if (filters.stage?.length)          filters.stage.forEach(s      => { const id = s?._id||s; if (id) q += `&stage=${id}`; });
      if (filters.project?.length)        filters.project.forEach(p    => { if (p?._id)  q += `&project=${p._id}`; });
      if (filters.developer?.length)      filters.developer.forEach(d  => { if (d?._id)  q += `&developer=${d._id}`; });
      if (filters.chanel?.length)         filters.chanel.forEach(c     => { if (c?._id)  q += `&channel=${c._id}`; });
      if (filters.sales?.length)          filters.sales.forEach(s      => { if (s?._id)  q += `&sales=${s._id}`; });
      if (filters.campaign?.length)       filters.campaign.forEach(c   => { if (c?._id)  q += `&campaign=${c._id}`; });
      if (filters.date?.from)             q += `&createdFrom=${encodeURIComponent(filters.date.from)}`;
      if (filters.date?.to)               q += `&createdTo=${encodeURIComponent(filters.date.to)}`;
      if (filters.stagedateupdated?.from) q += `&stageDateFrom=${encodeURIComponent(filters.stagedateupdated.from)}`;
      if (filters.stagedateupdated?.to)   q += `&stageDateTo=${encodeURIComponent(filters.stagedateupdated.to)}`;
      if (filters.teamLeader?.length)     filters.teamLeader.forEach(t => { if (t?._id)  q += `&teamLeader=${t._id}`; });
      await dispatch(GetallLeads_data_By_Managers_Email(userlogin.data.email, q));
    } catch { Notify('error', 'Failed to load leads', 'error'); }
    finally { setLoading(false); }
  };

  useEffect(() => { if (userlogin?.data?.email) { loadLeads(); dispatch(GetallProjectsdata()); } }, [userlogin]);

  // URL params
  useEffect(() => {
    const sp = new URLSearchParams(location.search);
    const stageParam   = sp.get('stage');
    const stageIdParam = sp.get('stageId');
    const noStageParam = sp.get('noStage');
    const managerFresh = sp.get('managerFresh');
    const managerPend  = sp.get('managerPending');
    const tlId         = sp.get('teamLeaderId');
    const salesId      = sp.get('salesId');
    const newF = { ...appliedFilters };
    let changed = false;
    if (stageParam) {
      if (noStageParam === 'true') { newF.stage = [{ name: 'No Stage', _id: null }]; changed = true; }
      else if (stageIdParam)       { newF.stage = [{ _id: stageIdParam, name: stageParam }]; changed = true; }
    }
    if (managerFresh === 'true') { newF.stage = [{ name: 'No Stage', _id: null }]; changed = true; }
    if (managerPend  === 'true') { newF.stage = [{ _id: stageIdParam||null, name: 'Pending' }]; changed = true; }
    if (tlId)    { newF.teamLeader = [{ _id: tlId }]; changed = true; }
    if (salesId) { newF.sales      = [{ _id: salesId }]; changed = true; }
    if (changed) { setAppliedFilters(newF); setTimeout(() => loadLeads(1, itemsPerPage, searchQuery, newF), 100); }
  }, [location.search]);

  // search from navbar
  useEffect(() => {
    const handler = () => {
      const v = localStorage.getItem('searchsalesstoragewithmanager') || '';
      if (v === lastSearchRef.current) return;
      lastSearchRef.current = v;
      setSearchQuery(v);
      localStorage.setItem(MANAGER_SEARCH_KEY, v);
      setCurrentPage(1);
      if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
      searchTimerRef.current = setTimeout(() => loadLeads(1, itemsPerPage, v, appliedFilters), 600);
    };
    window.addEventListener('searchsalesmanagerUpdated', handler);
    handler();
    return () => { window.removeEventListener('searchsalesmanagerUpdated', handler); if (searchTimerRef.current) clearTimeout(searchTimerRef.current); };
  }, [userlogin, itemsPerPage]);

  // filters effect
  useEffect(() => {
    if (filtersTimerRef.current) clearTimeout(filtersTimerRef.current);
    if (Object.keys(appliedFilters).length > 0) {
      filtersTimerRef.current = setTimeout(() => { setCurrentPage(1); loadLeads(1, itemsPerPage, searchQuery, appliedFilters); }, 400);
    }
    return () => { if (filtersTimerRef.current) clearTimeout(filtersTimerRef.current); };
  }, [appliedFilters]);

  const leadsArray = getLeadsArray();
  const pagination = getPagination();

  const displayLeads = useMemo(() => {
    if (!leadsArray.length || !sortConfig.key) return leadsArray;
    return [...leadsArray].sort((a, b) => {
      const get = l => sortConfig.key === 'createdAt' ? new Date(l.date||0) : new Date(l.stagedateupdated||0);
      return sortConfig.direction === 'ascending' ? get(a) - get(b) : get(b) - get(a);
    });
  }, [leadsFromRedux, sortConfig]);

  // selection
  const handleSelectLead = useCallback((id, e) => {
    if (e) e.stopPropagation();
    setSelectedLeads(prev => {
      const next = prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id];
      localStorage.setItem('selectedleedsbymanagers2', JSON.stringify(next));
      selectedLeadsRef.current = next;
      return next;
    });
  }, []);

  const handleSelectAll = useCallback(e => {
    if (e) e.stopPropagation();
    setSelectedLeads(prev => {
      if (prev.length === displayLeads.length) {
        localStorage.setItem('selectedleedsbymanagers2', JSON.stringify([]));
        selectedLeadsRef.current = [];
        return [];
      }
      const all = displayLeads.map(l => l._id);
      localStorage.setItem('selectedleedsbymanagers2', JSON.stringify(all));
      selectedLeadsRef.current = all;
      return all;
    });
  }, [displayLeads]);

  useEffect(() => { localStorage.setItem('selectedleedsbymanagers2', JSON.stringify(selectedLeads)); }, [selectedLeads]);

  // clear all
  const handleClearAll = () => {
    setAppliedFilters({}); setHasFilters(false); setSearchQuery(''); setCurrentPage(1);
    localStorage.removeItem(MANAGER_SEARCH_KEY); localStorage.removeItem('searchsalesstoragewithmanager');
    lastSearchRef.current = '';
    loadLeads(1, itemsPerPage, '');
    Notify('success', 'All filters cleared', 'success');
  };

  // receive
  const handleReceiveLead = async () => {
    if (!selectedLeadForReceive) return;
    setReceiveLoading(true);
    try {
      await dispatch(UpdateClientActionToReaciveusertolead(selectedLeadForReceive._id, { assign: false }));
      Notify('success', 'Lead received successfully', 'success');
      setShowReceiveModal(false); setSelectedLeadForReceive(null);
      setTimeout(() => loadLeads(currentPage, itemsPerPage, searchQuery, appliedFilters), 100);
    } catch { Notify('error', 'Failed to receive lead', 'error'); }
    finally { setReceiveLoading(false); }
  };

  // edit lead
  const handleShowUpdateLead = lead => {
    setSelectedLeadId(lead._id);
    setLeadnameupdate(lead.name || '');
    setPhonenumber2(lead.phonenumber2 || '');
    setWhatsappnumber(lead.whatsappnumber || '');
    setProjectupdatelead(lead.project ? (resproject?.data?.find(p => p._id === lead.project._id) || lead.project) : null);
    setShowUpdateLead(true);
  };
  const handleUpdateLead = async e => {
    e.preventDefault();
    if (!selectedLeadId || !leadnameupdate) return;
    setLoadingUpdate(true); setLoadingUpdate2(true);
    try { await dispatch(UpdateClientAction(selectedLeadId, { name: leadnameupdate, phonenumber2, whatsappnumber, project: projectupdatelead })); }
    catch { Notify('error', 'Failed to update lead', 'error'); }
    finally { setLoadingUpdate(false); setLoadingUpdate2(false); }
  };
  useEffect(() => {
    if (!loadingUpdate && resupdatelead?.status === 200) {
      Notify('success', 'Lead updated successfully', 'success');
      setShowUpdateLead(false);
      loadLeads(currentPage, itemsPerPage, searchQuery);
    }
  }, [loadingUpdate]);

  // assign success
  const handleAssignSuccess = useCallback(assignedLeads => {
    setSelectedLeads(prev => {
      const next = prev.filter(id => !assignedLeads.includes(id));
      localStorage.setItem('selectedleedsbymanagers2', JSON.stringify(next));
      selectedLeadsRef.current = next;
      return next;
    });
    setTimeout(() => loadLeads(currentPage, itemsPerPage, searchQuery), 500);
  }, [currentPage, itemsPerPage, searchQuery]);

  const refresh = () => { setCurrentPage(1); setSelectedLeads([]); loadLeads(1, itemsPerPage); };

  return (
    <LocalizationProvider dateAdapter={AdapterMoment}>
      <Box sx={{ ml: isMobile ? `${SIDEBAR_COLLAPSED}px` : `${sidebarW}px`, mt: '64px', minHeight: 'calc(100vh - 64px)', bgcolor: C.bg, transition: 'margin-left 0.25s cubic-bezier(0.4,0,0.2,1)' }}>
        <Box sx={{ p: isMobile ? 2 : 3.5 }}>

          {/* ── Header ── */}
          <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2, mb: 3 }}>
            <Box>
              <Typography sx={{ fontFamily: "'DM Sans', sans-serif", fontWeight: 800, fontSize: isMobile ? '28px' : '36px', color: C.text, lineHeight: 1.15, letterSpacing: '-0.02em' }}>
                Manager<br />Leads Center
              </Typography>
              <Typography sx={{ fontSize: '13.5px', color: C.textMuted, fontFamily: "'DM Sans', sans-serif", mt: 1 }}>
                Welcome back, <strong style={{ color: C.text }}>{userlogin?.data?.name || 'Manager'}</strong> — manage and track your team's leads.
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', gap: '12px', flexWrap: 'wrap', alignItems: 'stretch' }}>
              <StatCard label="Total Leads"  value={pagination?.totalItems} />
              <StatCard label="Pages"        value={pagination?.NumberOfPages || pagination?.totalPages} />
              <StatCard label="Current Page" value={currentPage} />
              <StatCard label="Per Page"     value={itemsPerPage} highlight />
            </Box>
          </Box>

          {/* ── Table Card ── */}
          <Box sx={{ bgcolor: C.white, borderRadius: '12px', border: `1px solid ${C.border}`, boxShadow: C.shadow, overflow: 'hidden' }}>

            {/* Toolbar */}
            <Box sx={{ px: 2.5, py: 2, display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap', borderBottom: `1px solid ${C.border}` }}>
              <Typography sx={{ fontFamily: "'DM Sans', sans-serif", fontWeight: 700, fontSize: '16px', color: C.text, flex: 1 }}>Live Pipeline</Typography>

              <Tooltip title="Newest first">
                <IconButton size="small" onClick={() => setSortConfig({ key: 'createdAt', direction: 'descending' })} sx={{ color: sortConfig.key === 'createdAt' && sortConfig.direction === 'descending' ? C.accent : C.textMuted, '&:hover': { color: C.accent } }}>
                  <ArrowDownwardIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <Tooltip title="Oldest first">
                <IconButton size="small" onClick={() => setSortConfig({ key: 'createdAt', direction: 'ascending' })} sx={{ color: sortConfig.key === 'createdAt' && sortConfig.direction === 'ascending' ? C.accent : C.textMuted, '&:hover': { color: C.accent } }}>
                  <ArrowUpwardIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              {sortConfig.key && (
                <Tooltip title="Clear sort">
                  <IconButton size="small" onClick={() => setSortConfig({ key: null, direction: 'ascending' })} sx={{ color: C.textMuted, '&:hover': { color: C.danger } }}>
                    <ClearIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}

              {(searchQuery || hasFilters) && (
                <Tooltip title="Clear all filters">
                  <IconButton size="small" onClick={handleClearAll} sx={{ color: C.danger, '&:hover': { bgcolor: C.dangerSoft } }}>
                    <ClearIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}

              <Box sx={{ width: '1px', height: '20px', bgcolor: C.border }} />
              <Tooltip title="Quick Filter"><IconButton size="small" sx={{ color: C.textMuted, '&:hover': { color: C.accent } }} onClick={() => setQuickFilter(true)}><FilterListIcon fontSize="small" /></IconButton></Tooltip>
              <Tooltip title="Refresh"><IconButton size="small" sx={{ color: C.textMuted, '&:hover': { color: C.accent } }} onClick={refresh} disabled={loading}><RefreshIcon fontSize="small" /></IconButton></Tooltip>
              <Tooltip title="Table settings"><IconButton size="small" sx={{ color: C.textMuted, '&:hover': { color: C.accent } }} onClick={() => setSettingsOpen(true)}><SettingsIcon fontSize="small" /></IconButton></Tooltip>
              <Box sx={{ width: '1px', height: '20px', bgcolor: C.border }} />
              <Button component={Link} to="/addclient" startIcon={<AddIcon />} size="small"
                sx={{ fontFamily: "'DM Sans', sans-serif", fontWeight: 600, fontSize: '13px', borderRadius: '8px', textTransform: 'none', px: '14px', py: '7px', bgcolor: C.accent, color: '#fff', border: 'none', '&:hover': { bgcolor: C.accentDark } }}>
                New Lead
              </Button>
              <Button startIcon={<AssignmentIcon />} size="small" onClick={() => setPrivateSheetOpen(true)}
                sx={{ fontFamily: "'DM Sans', sans-serif", fontWeight: 600, fontSize: '13px', borderRadius: '8px', textTransform: 'none', px: '14px', py: '7px', bgcolor: C.white, color: C.text, border: `1px solid ${C.border}`, '&:hover': { bgcolor: C.bg, borderColor: C.accent, color: C.accent } }}>
                Private Sheet
              </Button>
            </Box>

            {/* Table */}
            <TableContainer sx={{ overflowX: 'auto' }}>
              <Table size="small" sx={{ minWidth: 700 }}>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ width: 52, minWidth: 52, py: '10px', px: '12px', borderBottom: `2px solid ${C.border}` }}>
                      <Checkbox size="small"
                        checked={selectedLeads.length === displayLeads.length && displayLeads.length > 0}
                        indeterminate={selectedLeads.length > 0 && selectedLeads.length < displayLeads.length}
                        onChange={handleSelectAll}
                        sx={{ color: C.textMuted }}
                      />
                    </TableCell>
                    {colConfig.filter(c => c.visible && c.key !== 'selection').map(col => (
                      <TableCell key={col.key} sx={{ width: col.w, minWidth: col.w, py: '10px', px: '12px', fontFamily: "'DM Sans', sans-serif", fontSize: '11.5px', fontWeight: 600, color: C.textMuted, letterSpacing: '0.05em', textTransform: 'uppercase', borderBottom: `2px solid ${C.border}`, whiteSpace: 'nowrap' }}>
                        {col.label}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>

                <TableBody>
                  {loading ? (
                    <TableRow><TableCell colSpan={colConfig.filter(c => c.visible).length + 1} align="center" sx={{ py: 6 }}><CircularProgress size={32} sx={{ color: C.accent }} /></TableCell></TableRow>
                  ) : !displayLeads.length ? (
                    <TableRow><TableCell colSpan={colConfig.filter(c => c.visible).length + 1} align="center" sx={{ py: 6 }}>
                      <Typography sx={{ color: C.textMuted, fontFamily: "'DM Sans', sans-serif" }}>No leads found</Typography>
                      {(searchQuery || hasFilters) && <Button size="small" onClick={handleClearAll} sx={{ mt: 1, color: C.accent }}>Clear filters</Button>}
                    </TableCell></TableRow>
                  ) : displayLeads.map(lead => (
                    <StyledRow key={lead._id}>
                      {/* Checkbox */}
                      <TableCell sx={{ px: '12px', py: '10px' }}>
                        <Checkbox size="small" checked={selectedLeads.includes(lead._id)} onChange={e => handleSelectLead(lead._id, e)} sx={{ color: C.textMuted, '&.Mui-checked': { color: C.accent } }} />
                      </TableCell>

                      {/* Name */}
                      {colConfig.find(c => c.key === 'name')?.visible && (
                        <TableCell sx={{ px: '12px', cursor: 'pointer' }} onClick={() => setSelectedLead(lead)}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                            <Avatar sx={{ width: 34, height: 34, fontSize: '12px', fontWeight: 700, bgcolor: C.accentSoft, color: C.accent, fontFamily: "'DM Sans', sans-serif" }}>{getInitials(lead.name)}</Avatar>
                            <Box>
                              <Typography sx={{ fontSize: '13.5px', fontWeight: 600, color: C.text, fontFamily: "'DM Sans', sans-serif", lineHeight: 1.3 }}>{lead.name}</Typography>
                              <Typography sx={{ fontSize: '12px', color: C.textMuted, fontFamily: "'DM Sans', sans-serif" }}>{lead.email}</Typography>
                            </Box>
                          </Box>
                        </TableCell>
                      )}

                      {/* Phone */}
                      {colConfig.find(c => c.key === 'phone')?.visible && (
                        <TableCell sx={{ px: '12px' }}><PhoneCell user={lead} /></TableCell>
                      )}

                      {/* Project */}
                      {colConfig.find(c => c.key === 'project')?.visible && (
                        <TableCell sx={{ px: '12px' }}>
                          <Typography sx={{ fontSize: '13px', fontWeight: 500, color: C.text, fontFamily: "'DM Sans', sans-serif" }}>{lead.project?.name || '—'}</Typography>
                        </TableCell>
                      )}

                      {/* Developer */}
                      {colConfig.find(c => c.key === 'developer')?.visible && (
                        <TableCell sx={{ px: '12px' }}>
                          <Typography sx={{ fontSize: '13px', color: C.textMuted, fontFamily: "'DM Sans', sans-serif" }}>{lead.developer?.name || lead.project?.developer?.name || '—'}</Typography>
                        </TableCell>
                      )}

                      {/* Campaign */}
                      {colConfig.find(c => c.key === 'campaign')?.visible && (
                        <TableCell sx={{ px: '12px' }}>
                          <Typography sx={{ fontSize: '13px', color: C.textMuted, fontFamily: "'DM Sans', sans-serif" }}>{lead.campaign?.CampainName || lead.campaign?.name || '—'}</Typography>
                          {lead.campaign?.redirectLink && (
                            <Box sx={{ display: 'flex', gap: '4px', mt: '4px' }}>
                              <IconButton size="small" onClick={e => { e.stopPropagation(); navigator.clipboard.writeText(lead.campaign.redirectLink); Notify('success', 'Link copied', 'success'); }} sx={{ width: 20, height: 20, color: C.textMuted, '&:hover': { color: C.accent } }}><ContentCopyIcon sx={{ fontSize: '13px' }} /></IconButton>
                              <IconButton size="small" onClick={e => { e.stopPropagation(); window.open(lead.campaign.redirectLink, '_blank'); }} sx={{ width: 20, height: 20, color: C.textMuted, '&:hover': { color: C.accent } }}><OpenInNewIcon sx={{ fontSize: '13px' }} /></IconButton>
                            </Box>
                          )}
                        </TableCell>
                      )}

                      {/* Channel */}
                      {colConfig.find(c => c.key === 'chanel')?.visible && (
                        <TableCell sx={{ px: '12px' }}>
                          <Typography sx={{ fontSize: '13px', color: C.textMuted, fontFamily: "'DM Sans', sans-serif" }}>
                            {lead.chanel?.name || (typeof lead.chanel === 'string' ? lead.chanel : '—')}
                          </Typography>
                        </TableCell>
                      )}

                      {/* Stage */}
                      {colConfig.find(c => c.key === 'stage')?.visible && (
                        <TableCell sx={{ px: '12px' }}><StageCell user={lead} /></TableCell>
                      )}

                      {/* Sales */}
                      {colConfig.find(c => c.key === 'sales')?.visible && (
                        <TableCell sx={{ px: '12px' }}>
                          <Box sx={{ display: 'flex', flexDirection: 'column', gap: '3px' }}>
                            <Typography sx={{ fontSize: '13px', fontWeight: lead.assigntype ? 700 : 500, color: lead.assigntype ? C.accent : C.text, fontFamily: "'DM Sans', sans-serif" }}>
                              {lead.assigntype ? `Team: ${lead.sales?.name || 'N/A'}` : (lead.sales?.name || 'Not Assigned')}
                            </Typography>
                            {(lead.assignDate || lead.lastdateassign) && (
                              <Box sx={{ display: 'flex', flexDirection: 'column', gap: '1px' }}>
                                <Typography sx={{ fontSize: '11px', color: C.textMuted, fontFamily: "'DM Sans', sans-serif", display: 'flex', alignItems: 'center', gap: '3px' }}>
                                  <svg width="9" height="9" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>
                                  {moment(lead.assignDate || lead.lastdateassign).format('DD/MM/YY HH:mm')}
                                </Typography>
                              </Box>
                            )}
                          </Box>
                        </TableCell>
                      )}

                      {/* Creation Date */}
                      {colConfig.find(c => c.key === 'Creation_date')?.visible && (
                        <TableCell sx={{ px: '12px' }}>
                          {lead.resetcreationdate ? (
                            <Typography sx={{ fontSize: '12px', color: C.textMuted, fontFamily: "'DM Sans', sans-serif", fontStyle: 'italic' }}>Not available</Typography>
                          ) : (
                            <>
                              <Typography sx={{ fontSize: '13px', color: C.text, fontFamily: "'DM Sans', sans-serif", fontWeight: 500 }}>{lead.date ? moment(lead.date).format('DD/MM/YY') : '—'}</Typography>
                              <Typography sx={{ fontSize: '11px', color: C.textMuted, fontFamily: "'DM Sans', sans-serif" }}>{lead.date ? moment(lead.date).format('HH:mm') : ''}</Typography>
                            </>
                          )}
                        </TableCell>
                      )}

                      {/* Actions */}
                      {colConfig.find(c => c.key === 'actions')?.visible && (
                        <TableCell sx={{ px: '12px' }}>
                          <Box sx={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                            <Tooltip title={lead.assign ? 'Receive lead' : 'Already received'}>
                              <span>
                                <Button size="small" disabled={!lead.assign}
                                  onClick={() => { setSelectedLeadForReceive(lead); setShowReceiveModal(true); }}
                                  sx={{ fontFamily: "'DM Sans', sans-serif", fontWeight: 600, fontSize: '12px', borderRadius: '7px', textTransform: 'none', px: '10px', py: '5px', bgcolor: lead.assign ? C.warning : C.border, color: lead.assign ? '#fff' : C.textMuted, border: 'none', position: 'relative', '&:hover': { bgcolor: lead.assign ? '#e68a00' : C.border }, '&.Mui-disabled': { bgcolor: '#F1F3F9', color: C.textMuted } }}>
                                  {lead.assign ? 'Receive' : 'Received'}
                                  {lead.assign && <Box sx={{ position: 'absolute', top: 2, right: 2, width: 6, height: 6, borderRadius: '50%', bgcolor: C.danger, border: '1px solid #fff' }} />}
                                </Button>
                              </span>
                            </Tooltip>
                            <Button size="small" onClick={() => handleShowUpdateLead(lead)}
                              sx={{ fontFamily: "'DM Sans', sans-serif", fontWeight: 600, fontSize: '12px', borderRadius: '7px', textTransform: 'none', px: '10px', py: '5px', bgcolor: C.white, color: C.text, border: `1px solid ${C.border}`, '&:hover': { borderColor: C.accent, color: C.accent, bgcolor: C.accentSoft } }}>
                              Edit
                            </Button>
                            <Button size="small" onClick={() => { setSelectedLeadforsheet(lead); setSheetOpen(true); }}
                              sx={{ fontFamily: "'DM Sans', sans-serif", fontWeight: 600, fontSize: '12px', borderRadius: '7px', textTransform: 'none', px: '10px', py: '5px', bgcolor: C.white, color: '#9c27b0', border: '1px solid rgba(156,39,176,0.3)', '&:hover': { bgcolor: 'rgba(156,39,176,0.08)', borderColor: '#9c27b0' } }}>
                              + Sheet
                            </Button>
                          </Box>
                        </TableCell>
                      )}
                    </StyledRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>

            {/* Pagination */}
            {!loading && displayLeads.length > 0 && (
              <Box sx={{ px: 2.5, py: 1.5, display: 'flex', alignItems: 'center', justifyContent: 'center', borderTop: `1px solid ${C.border}`, flexWrap: 'wrap', gap: 1.5, bgcolor: C.bg, position: 'fixed', bottom: 0, borderRadius: '0px 25px 25px 0px', zIndex: 10, boxShadow: '0 -2px 8px rgba(16,24,40,0.06)' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography sx={{ fontSize: '13px', color: C.textMuted, fontFamily: "'DM Sans', sans-serif", whiteSpace: 'nowrap' }}>Rows per page:</Typography>
                  <TextField select value={itemsPerPage} onChange={e => { setItemsPerPage(e.target.value); setCurrentPage(1); loadLeads(1, e.target.value); }} size="small" sx={{ width: 72, '& .MuiOutlinedInput-root': { borderRadius: '8px', fontSize: '13px' } }}>
                    {[10, 25, 50, 100].map(n => <MenuItem key={n} value={n} sx={{ fontFamily: "'DM Sans', sans-serif", fontSize: '13px' }}>{n}</MenuItem>)}
                  </TextField>
                </Box>
                <Typography sx={{ fontSize: '13px', color: C.textMuted, fontFamily: "'DM Sans', sans-serif" }}>
                  Showing <Box component="span" sx={{ fontWeight: 700, color: C.text }}>{displayLeads.length}</Box> of <Box component="span" sx={{ fontWeight: 700, color: C.text }}>{pagination?.totalItems || displayLeads.length}</Box>
                </Typography>
                {(pagination?.NumberOfPages || pagination?.totalPages) > 1 && (
                  <Pagination
                    count={pagination?.NumberOfPages || pagination?.totalPages}
                    page={currentPage} size="small" showFirstButton showLastButton
                    onChange={(_, v) => { setCurrentPage(v); loadLeads(v, itemsPerPage); window.scrollTo({ top: 0, behavior: 'smooth' }); }}
                    sx={{ '& .MuiPaginationItem-root': { fontFamily: "'DM Sans', sans-serif", fontSize: '13px', borderRadius: '6px', color: C.textMuted }, '& .Mui-selected': { bgcolor: `${C.accent} !important`, color: '#fff', fontWeight: 700 }, '& .MuiPaginationItem-root:hover': { bgcolor: C.accentSoft } }}
                  />
                )}
              </Box>
            )}
          </Box>
        </Box>

        {/* ── Snackbar ── */}
        <Snackbar open={selectedLeads.length > 0} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
          message={`${selectedLeads.length} lead(s) selected`}
          action={
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
              <Button size="small" startIcon={<SendIcon sx={{ fontSize: 16 }} />} onClick={() => setAssignManagerDialogOpen(true)}
                sx={{ bgcolor: C.accent, color: '#fff', fontSize: '12px', fontWeight: 700, borderRadius: '8px', px: 2, '&:hover': { bgcolor: C.accentDark } }}>
                Assign
              </Button>
              <Button size="small" onClick={() => setSelectedLeads([])} sx={{ color: '#fff', fontSize: '12px' }}>Clear</Button>
            </Box>
          }
        />

        {/* ── Modals ── */}
        <ManagerQuickFilterModal open={quickFilter} onClose={() => setQuickFilter(false)}
          onApplyFilters={f => {
            setAppliedFilters(f);
            const active = Object.keys(f).some(k => Array.isArray(f[k]) ? f[k].length > 0 : typeof f[k] === 'object' ? f[k]?.from || f[k]?.to : false);
            setHasFilters(active);
            setCurrentPage(1);
            if (filtersTimerRef.current) clearTimeout(filtersTimerRef.current);
            filtersTimerRef.current = setTimeout(() => loadLeads(1, itemsPerPage, searchQuery, f), 100);
          }}
          initialFilters={appliedFilters}
          userlogin={userlogin}
        />

        {selectedLead && (
          <ShowClientsActionsAdvanced user={selectedLead} leadid={selectedLead._id} onClose={() => setSelectedLead(null)}
            onLeadUpdated={() => loadLeads(currentPage, itemsPerPage, searchQuery)} />
        )}

        {assignManagerDialogOpen && (
          <AssignLeadsFromManagerModel
            show={assignManagerDialogOpen}
            onHide={() => setAssignManagerDialogOpen(false)}
            selectedLeadsmanager={selectedLeads}
            onAssignSuccess={handleAssignSuccess}
            refreshdata={loadLeads}
          />
        )}

        <ShowUserSheet open={sheetOpen} onClose={() => setSheetOpen(false)} lead={selectedLeadforsheet} userlogin={userlogin} />
        <PrivateSheetForUser open={privateSheetOpen} onClose={() => setPrivateSheetOpen(false)} userlogin={userlogin} />

        {/* ── Receive Dialog ── */}
        <Dialog open={showReceiveModal} onClose={() => { setShowReceiveModal(false); setSelectedLeadForReceive(null); }} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: '16px' } }}>
          <DialogTitle sx={{ bgcolor: C.accent, color: '#fff', fontFamily: "'DM Sans', sans-serif", fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            Receive Lead
            <IconButton onClick={() => { setShowReceiveModal(false); setSelectedLeadForReceive(null); }} sx={{ color: '#fff' }}><CloseIcon /></IconButton>
          </DialogTitle>
          <DialogContent sx={{ pt: 3 }}>
            {selectedLeadForReceive && (
              <Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3, p: 2, bgcolor: C.accentSoft, borderRadius: '10px' }}>
                  <Avatar sx={{ width: 48, height: 48, bgcolor: C.accent, fontFamily: "'DM Sans', sans-serif", fontWeight: 700 }}>{getInitials(selectedLeadForReceive.name)}</Avatar>
                  <Box>
                    <Typography sx={{ fontFamily: "'DM Sans', sans-serif", fontWeight: 700, fontSize: '16px', color: C.text }}>{selectedLeadForReceive.name}</Typography>
                    {selectedLeadForReceive.phone && <Typography sx={{ fontFamily: "'DM Sans', sans-serif", fontSize: '13px', color: C.textMuted }}>{selectedLeadForReceive.phone}</Typography>}
                  </Box>
                </Box>
                <Alert severity="info">By receiving this lead, you take full responsibility for following up.</Alert>
              </Box>
            )}
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 3 }}>
            <Button onClick={() => { setShowReceiveModal(false); setSelectedLeadForReceive(null); }}>Cancel</Button>
            <Button variant="contained" onClick={handleReceiveLead} disabled={receiveLoading} sx={{ bgcolor: C.accent, '&:hover': { bgcolor: C.accentDark } }}>
              {receiveLoading ? <CircularProgress size={16} color="inherit" /> : 'Receive Lead'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* ── Edit Lead Dialog ── */}
        <Dialog open={showUpdateLead} onClose={() => setShowUpdateLead(false)} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: '16px' } }}>
          <DialogTitle sx={{ fontFamily: "'DM Sans', sans-serif", fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}><EditNoteIcon sx={{ color: C.accent }} />Edit Lead</Box>
            <IconButton onClick={() => setShowUpdateLead(false)} size="small"><CloseIcon fontSize="small" /></IconButton>
          </DialogTitle>
          <DialogContent sx={{ pt: 2 }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5, mt: 1 }}>
              <TextField fullWidth label="Lead Name *" value={leadnameupdate || ''} onChange={e => setLeadnameupdate(e.target.value)} size="small" error={!leadnameupdate} helperText={!leadnameupdate ? 'Required' : ''} sx={{ '& .MuiOutlinedInput-root': { borderRadius: '8px' } }} />
              <Box>
                <Typography sx={{ fontSize: '13px', fontWeight: 600, color: C.textMuted, mb: 1, fontFamily: "'DM Sans', sans-serif" }}>Phone 2 (optional)</Typography>
                <PhoneInput country="ae" value={phonenumber2 || ''} onChange={v => { setPhonenumber2(v); setErrPhone2(''); }} preferredCountries={['ae','sa','qa']} enableSearch inputStyle={{ width: '100%', borderRadius: '8px', fontSize: '13px' }} />
                {errPhone2 && <Typography sx={{ fontSize: '12px', color: C.danger, mt: 0.5 }}>{errPhone2}</Typography>}
              </Box>
              <Box>
                <Typography sx={{ fontSize: '13px', fontWeight: 600, color: C.textMuted, mb: 1, fontFamily: "'DM Sans', sans-serif" }}>WhatsApp (optional)</Typography>
                <PhoneInput country="ae" value={whatsappnumber || ''} onChange={v => { setWhatsappnumber(v); setErrWhatsapp(''); }} preferredCountries={['ae','sa','qa']} enableSearch inputStyle={{ width: '100%', borderRadius: '8px', fontSize: '13px' }} />
                {errWhatsapp && <Typography sx={{ fontSize: '12px', color: C.danger, mt: 0.5 }}>{errWhatsapp}</Typography>}
              </Box>
              {resproject?.data && (
                <Autocomplete options={resproject.data} getOptionLabel={o => o.name || ''} value={projectupdatelead || null} onChange={(_, v) => setProjectupdatelead(v)} isOptionEqualToValue={(o, v) => o._id === v?._id}
                  renderInput={params => <TextField {...params} label="Project *" size="small" error={!projectupdatelead} helperText={!projectupdatelead ? 'Required' : ''} sx={{ '& .MuiOutlinedInput-root': { borderRadius: '8px' } }} />}
                />
              )}
            </Box>
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 3 }}>
            <Button onClick={() => setShowUpdateLead(false)}>Cancel</Button>
            <Button variant="contained" onClick={handleUpdateLead} disabled={loadingUpdate2 || !leadnameupdate || !projectupdatelead} sx={{ bgcolor: C.accent, '&:hover': { bgcolor: C.accentDark } }}>
              {loadingUpdate2 ? <CircularProgress size={16} color="inherit" /> : 'Save Changes'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* ── Settings Drawer ── */}
        <Drawer anchor="right" open={settingsOpen} onClose={() => setSettingsOpen(false)} PaperProps={{ sx: { width: 360, bgcolor: '#F4F6FB !important', display: 'flex', flexDirection: 'column' } }}>
          <Box sx={{ px: 2.5, py: 2, bgcolor: C.white, borderBottom: `1px solid ${C.border}`, display: 'flex', alignItems: 'center', gap: 1 }}>
            <IconButton onClick={() => setSettingsOpen(false)} size="small"><CloseIcon fontSize="small" /></IconButton>
            <Typography sx={{ fontWeight: 700, fontSize: '17px', fontFamily: "'DM Sans', sans-serif", color: C.text, flex: 1 }}>Column Settings</Typography>
          </Box>
          <Box sx={{ flex: 1, overflowY: 'auto', px: 2.5, pt: 2.5, pb: 3 }}>
            <DragDropContext onDragEnd={onDragEnd}>
              <Droppable droppableId="manager-cols">
                {provided => (
                  <Box ref={provided.innerRef} {...provided.droppableProps} sx={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                    {colConfig.filter(c => c.key !== 'selection').map((col, index) => (
                      <Draggable key={col.key} draggableId={col.key} index={index}>
                        {(provided, snapshot) => (
                          <Box ref={provided.innerRef} {...provided.draggableProps}
                            sx={{ display: 'flex', alignItems: 'center', gap: 1.5, px: 2, py: 1.5, bgcolor: snapshot.isDragging ? '#E8ECFF' : C.white, borderRadius: '14px', border: `1.5px solid ${snapshot.isDragging ? C.accent : C.border}`, boxShadow: snapshot.isDragging ? '0 8px 24px rgba(13,71,161,0.18)' : '0 1px 3px rgba(16,24,40,0.06)' }}>
                            <IconButton size="small" onClick={() => toggleColVisibility(col.key)} sx={{ width: 34, height: 34, borderRadius: '50%', color: col.visible ? C.accent : C.textMuted, bgcolor: col.visible ? C.accentSoft : 'rgba(123,130,154,0.08)' }}>
                              {col.visible ? <VisibilityIcon sx={{ fontSize: '17px' }} /> : <VisibilityOffIcon sx={{ fontSize: '17px' }} />}
                            </IconButton>
                            <Typography sx={{ flex: 1, fontSize: '14px', fontWeight: 600, color: col.visible ? C.text : C.textMuted, fontFamily: "'DM Sans', sans-serif", textDecoration: col.visible ? 'none' : 'line-through' }}>{col.label}</Typography>
                            <Box sx={{ width: 48, height: 36, bgcolor: '#F0F2F9', borderRadius: '8px', display: 'flex', alignItems: 'center' }}>
                              <input type="number" value={col.w} onChange={e => updateColWidth(col.key, e.target.value)} onClick={e => e.stopPropagation()} style={{ width: '100%', height: '100%', border: 'none', outline: 'none', background: 'transparent', textAlign: 'center', fontSize: '13px', fontWeight: 600, color: C.text, fontFamily: "'DM Sans', sans-serif" }} />
                            </Box>
                            <Box {...provided.dragHandleProps} sx={{ display: 'flex', flexDirection: 'column', gap: '3px', cursor: 'grab', px: '2px' }}>
                              {[0,1,2].map(r => <Box key={r} sx={{ display: 'flex', gap: '3px' }}>{[0,1].map(c => <Box key={c} sx={{ width: 4, height: 4, borderRadius: '50%', bgcolor: C.textMuted, opacity: 0.6 }} />)}</Box>)}
                            </Box>
                          </Box>
                        )}
                      </Draggable>
                    ))}
                    {provided.placeholder}
                  </Box>
                )}
              </Droppable>
            </DragDropContext>
          </Box>
          <Box sx={{ px: 2.5, py: 2, borderTop: `1px solid ${C.border}`, bgcolor: C.white, display: 'flex', gap: 1 }}>
            <Button fullWidth variant="outlined" onClick={() => setColConfig(DEFAULT_COLS)} sx={{ borderColor: C.border, color: C.textMuted, fontFamily: "'DM Sans', sans-serif", textTransform: 'none', borderRadius: '10px' }}>Reset</Button>
            <Button fullWidth variant="contained" onClick={() => setSettingsOpen(false)} sx={{ bgcolor: C.accent, '&:hover': { bgcolor: C.accentDark }, textTransform: 'none', fontFamily: "'DM Sans', sans-serif", borderRadius: '10px' }}>Done</Button>
          </Box>
        </Drawer>
      </Box>
    </LocalizationProvider>
  );
};